/**
 * Tax Calculator for B2C Sales
 * 
 * Simplified logic (OSS under 10.000€ threshold):
 * - Germany + EU: 19% MwSt (German standard rate applies to all EU under threshold)
 * - Non-EU: 0% (tax-free export § 4 Nr. 1a UStG)
 * 
 * When EU revenue exceeds 10.000€/year, OSS registration is required
 * and each country's local VAT rate must be applied instead.
 * 
 * Prices are always displayed as gross (inclusive of tax).
 * The gross price remains constant regardless of destination.
 */

export interface TaxCalculation {
  grossPrice: number;        // End price (always constant)
  netPrice: number;          // Price without tax
  taxAmount: number;         // Tax amount
  taxRate: number;           // Applied tax rate in %
  taxName: string;           // Name of the tax (MwSt, VAT, etc.)
  isEU: boolean;             // Is EU country
  showTax: boolean;          // Whether to display tax on invoice
  legalNote?: string;        // Legal note for invoice
  productTaxType: 'standard' | 'reduced';  // Which tax type was applied
}

export interface TaxRateData {
  country_code: string;
  country_name: string;
  tax_rate: number;          // Standard rate
  reduced_rate?: number;     // Reduced rate (kept for future use)
  tax_name: string;
  is_eu: boolean;
  tax_type?: string;
  notes?: string;
}

// German standard rate - used for all DE + EU sales (under 10k threshold)
const GERMAN_STANDARD_RATE = 19;

/**
 * Calculate tax for a given gross price and customer country.
 * 
 * useLocalRates = false (default, under 10k OSS threshold):
 *   German 19% applies to all EU countries. Non-EU = 0%.
 * 
 * useLocalRates = true (above 10k OSS threshold):
 *   Each EU country's local VAT rate from the tax_rates table is used.
 *   Non-EU = 0%. Germany still uses 19%.
 */
export function calculateTax(
  grossPrice: number,
  countryCode: string,
  taxRates: TaxRateData[],
  isB2B: boolean = false,
  productTaxType: 'standard' | 'reduced' = 'standard',
  useLocalRates: boolean = false
): TaxCalculation {
  const countryRate = taxRates.find(r => r.country_code === countryCode);
  const isEU = countryRate?.is_eu ?? isEUCountryCode(countryCode);

  // B2B reverse charge for EU (except Germany)
  if (isB2B && isEU && countryCode !== 'DE') {
    const netPrice = grossPrice;
    return {
      grossPrice,
      netPrice: Math.round(netPrice * 100) / 100,
      taxAmount: 0,
      taxRate: 0,
      taxName: countryRate?.tax_name || 'MwSt',
      isEU: true,
      showTax: false,
      legalNote: 'Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge)',
      productTaxType,
    };
  }

  let taxRateValue: number;
  let taxName: string;
  let legalNote: string | undefined;

  if (isEU) {
    if (useLocalRates && countryCode !== 'DE') {
      // OSS mode: use the destination country's local VAT rate
      taxRateValue = countryRate?.tax_rate ?? GERMAN_STANDARD_RATE;
      taxName = countryRate?.tax_name || 'VAT';
      legalNote = `${taxName} ${taxRateValue}% gem. §3c Abs. 1 UStG (Lieferung ins Bestimmungsland)`;
    } else {
      // Under threshold or Germany: German 19%
      taxRateValue = GERMAN_STANDARD_RATE;
      taxName = 'MwSt';
      if (countryCode === 'DE') {
        legalNote = 'Umsatzsteuer gemäß § 12 Abs. 1 UStG';
      } else {
        legalNote = 'Umsatzsteuer gemäß § 12 Abs. 1 UStG';
      }
    }
  } else {
    // Non-EU: 0% export
    taxRateValue = 0;
    taxName = countryRate?.tax_name || 'VAT';
    legalNote = 'Steuerfreie Ausfuhrlieferung gemäß § 4 Nr. 1a UStG';
  }

  const taxMultiplier = 1 + (taxRateValue / 100);
  const netPrice = grossPrice / taxMultiplier;
  const taxAmount = grossPrice - netPrice;

  return {
    grossPrice,
    netPrice: Math.round(netPrice * 100) / 100,
    taxAmount: Math.round(taxAmount * 100) / 100,
    taxRate: taxRateValue,
    taxName,
    isEU,
    showTax: taxRateValue > 0,
    legalNote,
    productTaxType,
  };
}

// EU country codes for fallback check
const EU_CODES = new Set([
  'AT','BE','BG','HR','CY','CZ','DK','EE','FI','FR','DE','GR',
  'HU','IE','IT','LV','LT','LU','MT','NL','PL','PT','RO','SK','SI','ES','SE'
]);

function isEUCountryCode(code: string): boolean {
  return EU_CODES.has(code);
}

/**
 * Calculate tax for a cart with items.
 * Under 10k threshold, all items use 19% German rate for EU, 0% for non-EU.
 */
export interface CartTaxBreakdown {
  items: {
    name: string;
    quantity: number;
    grossPrice: number;
    taxType: 'standard' | 'reduced';
  }[];
  countryCode: string;
  taxRates: TaxRateData[];
  isB2B?: boolean;
}

export interface CartTaxResult {
  totalGross: number;
  totalNet: number;
  taxBreakdown: {
    taxType: 'standard' | 'reduced';
    taxRate: number;
    taxName: string;
    taxAmount: number;
    netAmount: number;
  }[];
  totalTax: number;
  legalNotes: string[];
}

export function calculateCartTax(breakdown: CartTaxBreakdown & { useLocalRates?: boolean }): CartTaxResult {
  const { items, countryCode, taxRates, isB2B = false, useLocalRates = false } = breakdown;

  const totalGross = items.reduce((sum, i) => sum + (i.grossPrice * i.quantity), 0);
  const calc = calculateTax(totalGross, countryCode, taxRates, isB2B, 'standard', useLocalRates);

  const taxBreakdown: CartTaxResult['taxBreakdown'] = [{
    taxType: 'standard',
    taxRate: calc.taxRate,
    taxName: calc.taxName,
    taxAmount: calc.taxAmount,
    netAmount: calc.netPrice,
  }];

  const legalNotes = calc.legalNote ? [calc.legalNote] : [];

  return {
    totalGross,
    totalNet: calc.netPrice,
    taxBreakdown,
    totalTax: calc.taxAmount,
    legalNotes,
  };
}

/**
 * Format tax display for checkout/cart
 */
export function formatTaxDisplay(calc: TaxCalculation): string {
  if (calc.taxRate === 0) {
    if (calc.isEU) {
      return 'inkl. 0% ' + calc.taxName;
    }
    return 'exkl. MwSt (Ausfuhrlieferung)';
  }

  return `inkl. ${calc.taxRate}% ${calc.taxName}`;
}

/**
 * Format tax amount for display
 */
export function formatTaxAmount(calc: TaxCalculation): string {
  if (calc.taxRate === 0) {
    return '0,00 €';
  }

  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(calc.taxAmount);
}

/**
 * Get invoice legal notice based on tax situation
 */
export function getInvoiceTaxNotice(calc: TaxCalculation): string | null {
  if (calc.legalNote) {
    return calc.legalNote;
  }

  if (!calc.isEU && calc.taxRate === 0) {
    return 'Steuerfreie Ausfuhrlieferung gemäß § 4 Nr. 1a UStG';
  }

  return null;
}

/**
 * Format currency for display
 */
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR'
  }).format(amount);
}
